import {
    Check,
    Copy,
    ExternalLink
} from 'lucide-react';
import {
    useState
} from 'react';
import {
    Button
} from 'shared/components/ui/button';

export interface ConfigField {
    label: string;
    value?: string;
    type?: 'masked' | 'string';
    copyable?: boolean;
    emptyText?: string;
    linkText?: string;
    linkUrl?: string;
}

export default function ConfigFieldsDisplay({
    fields
} : { fields: ConfigField[] }) {
    const [copiedField, setCopiedField] = useState<string | null>(null);

    const handleCopy = (value: string, fieldName: string) => {
        navigator.clipboard.writeText(value);
        setCopiedField(fieldName);
        setTimeout(() => {
            setCopiedField(null);
        }, 2000);
    };

    const renderFieldValue = (field: ConfigField) => {
        if (!field.value && field.emptyText) {
            return <span className="text-muted-foreground">{field.emptyText}</span>;
        }

        switch (field.type) {
            case 'masked':
                return (
                    <div className="flex items-center gap-3">
                        <span className="font-mono tracking-wider">{'•'.repeat(30)}</span>
                        {field.linkText && field.linkUrl && (
                            <>
                                <span className="text-muted-foreground">|</span>
                                <a href={field.linkUrl} className="flex items-center gap-1.5 text-sm text-primary hover:underline">
                                    {field.linkText}
                                    <ExternalLink className="size-3.5" />
                                </a>
                            </>
                        )}
                    </div>
                );
            default:
                return <span className="text-[14px] font-[400]">{field.value}</span>;
        }
    };

    return (
        <div>
            <div className="space-y-3">
                <div className="space-y-6">
                    {fields.map((field, index) => (
                        <div key={index} className="flex items-center gap-6">
                            <div className="w-40 shrink-0">
                                <span className="text-sm text-muted-foreground">{field.label}</span>
                            </div>
                            <div className="flex flex-1 items-center gap-2 truncate">
                                <div className="flex-1 truncate">{renderFieldValue(field)}</div>
                                {field.copyable && field.value && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="size-8 shrink-0"
                                        onClick={() => handleCopy(field?.value || '', field.label)}
                                    >
                                        {copiedField === field.label ? (
                                            <Check className="size-4 text-green-500" />
                                        ) : (
                                            <Copy className="size-4" />
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
